import { useMutation } from '@tanstack/react-query'
import { ViewEmployee } from '@xeetr/types/models'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import {
  createOptimisticUpdate,
  invalidateEmployeeQueries,
  rollbackOptimisticUpdate,
} from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import { EmployeeService } from '../services/Employee.service'

import { useTenantRequiredService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseEmployeesMutationReturn {
  createEmployee: (employee: ViewEmployee) => void
  updateEmployee: (params: { id: string; employee: Partial<ViewEmployee> }) => void
  reorderEmployee: (params: { id: string; employee: Partial<ViewEmployee> }) => void
  deleteEmployee: (id: string) => void
  isCreating: boolean
  isUpdating: boolean
  isReordering: boolean
  isDeleting: boolean
}

/**
 * Hook for managing employee mutations (create, update, delete).
 *
 * Features:
 * - Granular PATCH updates for individual employees
 * - Optimistic cache updates
 * - Automatic cache invalidation
 * - Toast notifications for success/error
 *
 * @example
 * ```tsx
 * const { updateEmployee, isUpdating } = useEmployeesMutation()
 *
 * updateEmployee({
 *   id: 'employee-123',
 *   employee: { name: 'Updated Name' }
 * })
 * ```
 */
export const useEmployeesMutation = (): UseEmployeesMutationReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const getService = useTenantRequiredService(EmployeeService)

  const createMutation = useMutation({
    mutationFn: async (employee: ViewEmployee) => {
      const service = await getService()
      return service.createEmployee(employee)
    },
    onSuccess: () => {
      invalidateEmployeeQueries(tenantId)
      showToast({ severity: 'success', detail: 'Mitarbeiter erfolgreich erstellt.' })
    },
    onError: (error) => {
      console.error('Employee create error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Erstellen des Mitarbeiters.',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, employee }: { id: string; employee: Partial<ViewEmployee> }) => {
      const service = await getService()
      return service.patchEmployee(id, employee)
    },
    onSuccess: () => {
      invalidateEmployeeQueries(tenantId)
      showToast({ severity: 'success', detail: 'Mitarbeiter erfolgreich aktualisiert.' })
    },
    onError: (error) => {
      console.error('Employee update error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Aktualisieren des Mitarbeiters.',
      })
    },
  })

  const reorderMutation = useMutation({
    mutationFn: async ({ id, employee }: { id: string; employee: Partial<ViewEmployee> }) => {
      const service = await getService()
      return service.patchEmployee(id, employee)
    },
    onMutate: async ({ id, employee }: { id: string; employee: Partial<ViewEmployee> }) => {
      return createOptimisticUpdate<ViewEmployee[]>(
        queryKeys.employees.all(tenantId),
        (oldEmployees) =>
          (oldEmployees ?? []).map((existingEmployee) =>
            existingEmployee.id === id ? { ...existingEmployee, ...employee } : existingEmployee
          )
      )
    },
    onError: (error, _variables, context) => {
      rollbackOptimisticUpdate(queryKeys.employees.all(tenantId), context)
      console.error('Employee reorder error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Verschieben des Mitarbeiters.',
      })
    },
    onSettled: () => {
      invalidateEmployeeQueries(tenantId)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const service = await getService()
      return service.deleteEmployee(id)
    },
    onSuccess: () => {
      invalidateEmployeeQueries(tenantId)
      showToast({ severity: 'success', detail: 'Mitarbeiter erfolgreich gelöscht.' })
    },
    onError: (error) => {
      console.error('Employee delete error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Löschen des Mitarbeiters.',
      })
    },
  })

  return {
    createEmployee: createMutation.mutate,
    updateEmployee: updateMutation.mutate,
    reorderEmployee: reorderMutation.mutate,
    deleteEmployee: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isReordering: reorderMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
